/**
 * Blog Card Component
 * Reusable card for blog listing pages
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { BlogPost } from '@/data/blogPosts';
import { trackBlogCardClick } from '@/services/analytics';

interface BlogCardProps {
  blog: BlogPost;
  position?: number; // Position in the list for analytics
}

export default function BlogCard({ blog, position = 0 }: BlogCardProps) {
  const navigate = useNavigate();
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCardClick = () => {
    // Track blog card click
    trackBlogCardClick({
      postId: blog.id,
      title: blog.title,
      position: position,
    });
    navigate(`/blog/${blog.slug}`);
  };

  const handleKeywordExpand = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setShowAllKeywords(!showAllKeywords);
  };

  const displayedKeywords = showAllKeywords ? blog.keywords : blog.keywords.slice(0, 3);

  return (
    <Card 
      className="h-full flex flex-col overflow-hidden hover:shadow-xl transition-all cursor-pointer group border-2 hover:border-primary/50"
      onClick={handleCardClick}
    >
      {/* Image */}
      <div className="aspect-video overflow-hidden relative">
        <img
          src={blog.imageUrl}
          alt={blog.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <CardHeader>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <Calendar className="h-4 w-4" />
          {formatDate(blog.publishedAt)}
        </div>
        <CardTitle className="line-clamp-2 text-lg sm:text-xl group-hover:text-primary transition-colors">
          {blog.title}
        </CardTitle>
        <CardDescription className="line-clamp-3">
          {blog.summary}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 mt-auto">
        {blog.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {displayedKeywords.map((keyword, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {keyword}
              </Badge>
            ))}
            {blog.keywords.length > 3 && (
              <Badge 
                variant="outline" 
                className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                onClick={handleKeywordExpand}
              >
                {showAllKeywords ? 'Show less' : `+${blog.keywords.length - 3}`}
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

